import {EditorSelection, type Extension, RangeSetBuilder, StateEffect, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import {clsx} from 'clsx'

import type {CstNode, NodeContext} from '../../types'
import {parseMarkdown, walkCst} from '../parse-markdown'
import {isListType} from '../utils'
import styles from './decorate-markdown.module.css'
import {LineBreakWidget} from './widgets/line-break'
import {ThematicBreakWidget} from './widgets/thematic-break'

function overlapsNode(selectionFrom: number, selectionTo: number, nodeFrom: number, nodeTo: number): boolean {
  return (
    (selectionFrom >= nodeFrom && selectionFrom < nodeTo) ||
    (selectionTo > nodeFrom && selectionTo <= nodeTo) ||
    (selectionFrom < nodeFrom && selectionTo > nodeTo)
  )
}

function getNodeMark(node: CstNode, active: boolean, source: string, context: NodeContext): Decoration | undefined {
  const blockClass = clsx(styles.block, active && styles.activeBlock)
  const {withinActiveTable, withinActiveList} = context
  switch (node.type) {
    case 'lineBreak':
      return Decoration.replace({widget: new LineBreakWidget()})
    case 'blockQuoteMarker':
    case 'strongSequence':
    case 'emphasisSequence':
    case 'codeTextSequence': // inline code delimiters
    case 'strikethroughSequence':
    case 'codeFencedFence':
    case 'codeTextPadding':
    case 'atxHeadingSequence': // ### heading indicator
    case 'setextHeadingLine': // === or --- heading underline
    case 'labelMarker': // brackets around link text
    case 'resource': // link (url) part
    case 'linePrefix': // indentation for indented code blocks
    case 'listItemIndent':
    case 'listItemPrefix':
    case 'tableCellDivider':
      return Decoration.mark({class: styles.delimiter})
    case 'codeFencedFenceInfo':
      return Decoration.mark({class: styles.codeInfo})
    case 'strong':
      return Decoration.mark({tagName: 'strong'})
    case 'emphasis':
      return Decoration.mark({tagName: 'em'})
    case 'codeText':
      return Decoration.mark({tagName: 'code'})
    case 'strikethrough':
      return Decoration.mark({tagName: 'del'})
    case 'link': {
      const destinationNode = node.children
        .find(n => n.type === 'resource')
        ?.children.find(n => n.type === 'resourceDestination')
      return Decoration.mark({
        tagName: 'a',
        attributes: {
          href: destinationNode ? source.slice(destinationNode.start, destinationNode.end) : '',
        },
      })
    }
    case 'thematicBreak':
      if (active) {
        // When active, show the original text
        return Decoration.mark({tagName: 'span'})
      } else {
        // When not active, replace with horizontal rule widget
        return Decoration.replace({
          widget: new ThematicBreakWidget(active),
          block: false,
        })
      }
    case 'blockQuote':
      return Decoration.mark({tagName: 'blockquote', class: blockClass})
    case 'codeFenced':
    case 'codeIndented':
      return Decoration.mark({tagName: 'pre', class: blockClass})
    case 'paragraph':
      return Decoration.mark({tagName: 'p', class: clsx(blockClass, styles.paragraph)})
    case 'atxHeading': {
      const sequence = node.children.find(n => n.type === 'atxHeadingSequence')
      if (!sequence) return undefined
      const level = sequence.end - sequence.start
      return Decoration.mark({tagName: `h${level}`, class: blockClass})
    }
    case 'setextHeading': {
      const sequence = node.children.find(n => n.type === 'setextHeadingLine')
      if (!sequence) return undefined
      const level = source.slice(sequence.start, sequence.end).trim().startsWith('=') ? 1 : 2
      return Decoration.mark({tagName: `h${level}`, class: blockClass})
    }
    case 'listUnordered':
    case 'listOrdered': {
      if (withinActiveList) return undefined
      const mark = Decoration.mark({tagName: node.type === 'listOrdered' ? 'ol' : 'ul', class: blockClass})
      /*
       * For decorations with the same 'from' and 'startSide' value, CodeMirror renders the first tag and wraps
       * the second tag around it. This is the case when rendering a list with one item. To force the <ul>/<ol>
       * to wrap around the <li>, we set the `startSide` to -1 which is lower than the <li> so that they are
       * rendered in the correct HTML syntax. Lower startSide = outermost element
       */
      mark.startSide = -1
      return mark
    }
    case 'listItem': {
      if (withinActiveList) return undefined
      const mark = Decoration.mark({tagName: 'li'})
      mark.startSide = 0
      return mark
    }
    case 'table':
      // tables revert to plain text when active (for now, until we build a table editor)
      return active
        ? Decoration.mark({tagName: 'pre', class: blockClass})
        : Decoration.mark({tagName: 'table', class: clsx(blockClass, styles.table)})
    case 'tableHead':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'thead'})
    case 'tableBody':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'tbody'})
    case 'tableRow':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'tr'})
    case 'tableHeader':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'th'})
    case 'tableData':
    case 'tableDelimiter':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'td'})
    case 'tableDelimiterRow':
      return withinActiveTable ? undefined : Decoration.mark({tagName: 'tr', class: styles.delimiter})
    default:
      return undefined
  }
}

function buildDecorations(source: string, tree: CstNode, selection: EditorSelection | null): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()

  const activeTables = new Set<CstNode>()
  const activeLists = new Set<CstNode>()
  walkCst(tree, (node, ancestors) => {
    const overlapsSelection =
      selection?.ranges.some(range => overlapsNode(range.from, range.to, node.start, node.end)) ?? false
    if (overlapsSelection && node.type === 'table') activeTables.add(node)
    if (overlapsSelection && isListType(node.type)) activeLists.add(node)

    const mark = getNodeMark(node, overlapsSelection, source, {
      withinActiveTable: ancestors.some(n => activeTables.has(n)),
      withinActiveList: ancestors.some(n => activeLists.has(n)),
    })

    if (mark) {
      builder.add(node.start, node.end, mark)
    }
  })

  return builder.finish()
}

interface MarkdownDecorationsState {
  tree: CstNode
  focused: boolean
  decorations: DecorationSet
}

const focusChangeEffect = StateEffect.define<boolean>()

const focusChangeExtension = EditorView.focusChangeEffect.of((_, focused) => focusChangeEffect.of(focused))

const markdownDecorationsState = StateField.define<MarkdownDecorationsState>({
  create: s => {
    const doc = s.doc.toString()
    const tree = parseMarkdown(doc)
    return {tree, decorations: buildDecorations(doc, tree, s.selection), focused: false}
  },
  update: (prev, tr) => {
    const focusChangeEvent = tr.effects.find(e => e.is(focusChangeEffect))

    const selectionChanged = !tr.startState.selection.eq(tr.newSelection)
    const focusChanged = focusChangeEvent !== undefined
    const docChanged = tr.docChanged

    if (!selectionChanged && !focusChanged && !docChanged) return prev

    const doc = tr.newDoc.toString()

    // avoid reparsing the tree if only the selection changes
    const tree = docChanged ? parseMarkdown(doc) : prev.tree

    const focused = focusChangeEvent?.value ?? prev.focused
    const selection = focused ? tr.newSelection : null

    return {
      tree,
      decorations: buildDecorations(doc, tree, selection),
      focused,
    }
  },
  provide: field => EditorView.decorations.from(field, state => state.decorations),
})

export const decorateMarkdownPlugin: Extension = [focusChangeExtension, markdownDecorationsState]
